<template>
  <!-- Banner with countdown -->
  <div :class="[
    'w-full py-2 px-4 shadow-md z-50',
    textColor,
    isSticky ? 'sticky top-0' : ''
  ]" :style="{ backgroundColor }">
    <div class="flex flex-col items-center justify-center mx-auto gap-x-4 md:gap-x-4 md:gap-y-4 gap-y-2 max-w-screen-2xl sm:flex-row">
      <div class="text-lg font-medium text-center md:text-xl md:text-center !leading-none sm:whitespace-nowrap font-ww-medium center-p text-balance" v-html="bannerText">
      </div>
      <div class="flex items-center justify-center w-full sm:w-auto">
        <div class="flex space-x-2 md:space-x-2">
          <div class="flex flex-col items-center">
            <span class="text-xl font-medium md:text-3xl !leading-none">{{ formatTime(days) }}</span>
            <span class="text-xs tracking-wide uppercase md:text-xs font-ww-light">{{ daysLabel }}</span>
          </div>

          <span class="mt-0 text-xl font-medium md:text-3xl">:</span>

          <div class="flex flex-col items-center">
            <span class="text-xl font-medium md:text-3xl !leading-none">{{ formatTime(hours) }}</span>
            <span class="text-xs tracking-wide uppercase md:text-xs font-ww-light">{{ hoursLabel }}</span>
          </div>

          <span class="mt-0 text-xl font-medium md:text-3xl">:</span>

          <div class="flex flex-col items-center">
            <span class="text-xl font-medium md:text-3xl !leading-none">{{ formatTime(minutes) }}</span>
            <span class="text-xs tracking-wide uppercase md:text-xs font-ww-light">{{ minutesLabel }}</span>
          </div>

          <span class="mt-0 text-xl font-medium md:text-3xl">:</span>

          <div class="flex flex-col items-center">
            <span class="text-xl font-medium md:text-3xl !leading-none animate-pulse">{{ formatTime(seconds) }}</span>
            <span class="text-xs tracking-wide uppercase md:text-xs font-ww-light">{{ secondsLabel }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  bannerText: {
    type: String,
    default: 'HURRY, LOCK IN THE <br class="hidden sm:block"> PRE-INFLATION PRICES.',
  },
  backgroundColor: {
    type: String,
    default: '#cb161b',
  },
  textColor: {
    type: String,
    default: 'text-white',
  },
  daysLabel: {
    type: String,
    default: 'Days',
  },
  hoursLabel: {
    type: String,
    default: 'Hours',
  },
  minutesLabel: {
    type: String,
    default: 'Minutes',
  },
  secondsLabel: {
    type: String,
    default: 'Seconds',
  },
  countdownTargetHour: {
    type: Number,
    default: 24,
  },
  isSticky: {
    type: Boolean,
    default: true,
  }
});

// Reactive countdown values
const days = ref(0)
const hours = ref(0)
const minutes = ref(0)
const seconds = ref(0)

let countdownInterval = null

// Format time to always show two digits (e.g., 09 instead of 9)
const formatTime = (time) => {
  return time < 10 ? `0${time}` : time.toString()
}

// Update the countdown timer
const updateCountdown = () => {
  const now = new Date()
  const midnight = new Date()
  midnight.setHours(props.countdownTargetHour, 0, 0, 0)
  
  // If we've passed midnight, set it to next midnight
  if (now >= midnight) {
    midnight.setDate(midnight.getDate() + 1)
  }
  
  const difference = midnight.getTime() - now.getTime()
  
  if (difference > 0) {
    days.value = Math.floor(difference / (1000 * 60 * 60 * 24))
    hours.value = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    minutes.value = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    seconds.value = Math.floor((difference % (1000 * 60)) / 1000)
  } else {
    // Reset to next day if time has passed
    days.value = 0
    hours.value = 0
    minutes.value = 0
    seconds.value = 0
  }
}

onMounted(() => {
  // Initialize countdown
  updateCountdown()
  
  // Update countdown every second
  countdownInterval = setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  // Clean up interval
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})

defineBuilderComponent({
  friendlyName: 'CountdownBanner',
  inputs: [
    {
      name: 'bannerText',
      type: 'html',
      friendlyName: 'Banner Text',
      defaultValue: 'HURRY, LOCK IN THE <br class="hidden sm:block"> PRE-INFLATION PRICES.',
    },
    {
      name: 'backgroundColor',
      type: 'color',
      friendlyName: 'Background Color',
      defaultValue: '#cb161b',
    },
    {
      name: 'textColor',
      type: 'text',
      friendlyName: 'Text Color Class',
      defaultValue: 'text-white',
    },
    {
      name: 'daysLabel',
      type: 'text',
      friendlyName: 'Days Label',
      defaultValue: 'Days',
    },
    {
      name: 'hoursLabel',
      type: 'text',
      friendlyName: 'Hours Label',
      defaultValue: 'Hours',
    },
    {
      name: 'minutesLabel',
      type: 'text',
      friendlyName: 'Minutes Label',
      defaultValue: 'Minutes',
    },
    {
      name: 'secondsLabel',
      type: 'text',
      friendlyName: 'Seconds Label',
      defaultValue: 'Seconds',
    },
    {
      name: 'countdownTargetHour',
      type: 'number',
      friendlyName: 'Countdown Target Hour (24 for midnight)',
      defaultValue: 24,
    },
    {
      name: 'isSticky',
      type: 'boolean',
      friendlyName: 'Sticky Position',
      defaultValue: true,
    },
  ],
});
</script> 

<style>

.center-p p {
  text-align: center;
}
</style>